import { formatDate } from "../../lib/dates.ts";

import type { SearchedDuplicate } from "./api/contract.ts";
import type { HeaderKind } from "./draft.ts";
import type { DecisionKind, SectionKey } from "./types.ts";

/**
 * Lo que se lee en la pantalla de digitación y NO manda el API. Los nombres de
 * estados, pasos, motivos y tipos de foto llegan del back y no se traducen
 * aquí.
 */

export const SECTION_LABELS: Record<SectionKey, string> = {
  documentData: "Datos de la factura",
  detail: "Detalle",
};

export const HEADER_LABELS: Record<HeaderKind, string> = {
  number: "Número",
  date: "Fecha",
};

export const HEADER_PLACEHOLDERS: Record<HeaderKind, string> = {
  number: "No se leyó",
  date: "dd/mm/aaaa",
};

export const DECISION_LABELS: Record<DecisionKind, string> = {
  valid: "Válida",
  invalid: "Inválida",
  forData: "Para información",
};

/** La decisión de una candidata a «Duplicada de…» (`SearchedDuplicate`). */
export const CANDIDATE_DECISION_LABELS: Record<
  NonNullable<SearchedDuplicate["decision"]>,
  string
> = {
  valid: "Válida",
  invalid: "Inválida",
  for_data: "Para información",
};

/** El panel de participantes y la tira de sus documentos. */
export const PARTICIPANT_TEXTS = {
  panel: "Participantes",
  search: "Buscar participante",
  searchPlaceholder: "Participante, grupo o factura",
  clearSearch: "Borrar la búsqueda",
  tabs: "Lo que propone el sistema",
  tabsHint: "← → cambian de pestaña",
  tabLabels: {
    "pre-aprobadas": "Pre-aprobadas",
    "pre-rechazadas": "Pre-rechazadas",
    "por-digitar": "Por digitar",
  },
  count: (count: number) =>
    count === 1 ? "1 participante" : `${String(count)} participantes`,
  sort: {
    label: "Ordenar",
    labelActive: (option: string) => `Ordenar: ${option}`,
    default: "Predeterminado",
    desc: "Meta: de más a menos avance",
    asc: "Meta: de menos a más avance",
    /** `ready`: ya subió la meta y le faltan válidas, lo más cerca primero. */
    ready: "Listos para aprobar",
  },
  none: "Ningún participante coincide con la búsqueda.",
  noneInTab: "No queda ningún participante con facturas pendientes aquí.",
  choose: "Elige un participante para ver sus facturas.",
  goal: (valid: number, goal: number) => `${String(valid)}/${String(goal)}`,
  goalTitle: (valid: number, goal: number, percent: number) =>
    `${String(valid)} válidas de una meta de ${String(goal)} (${String(percent)} %)`,
  pending: (count: number) =>
    count === 1 ? "1 pendiente" : `${String(count)} pendientes`,
  /** Cuántas subió este mes, en cualquier estado. */
  uploaded: (count: number) =>
    count === 1 ? "1 subida" : `${String(count)} subidas`,
  summaryGoal: (goal: number) => `Meta ${String(goal)}`,
  valid: (count: number) =>
    count === 1 ? "1 válida" : `${String(count)} válidas`,
  invalid: (count: number) =>
    count === 1 ? "1 inválida" : `${String(count)} inválidas`,
  forInformation: (count: number) => `${String(count)} para información`,
  missing: (count: number) =>
    count === 1 ? "Le falta 1" : `Le faltan ${String(count)}`,
  goalReached: "Cumplió la meta",
  progressOf: (name: string) => `Avance de ${name} hacia su meta`,
  documents: "Sus facturas",
  documentsEmpty: "No le quedan facturas por digitar.",
  documentOfTotal: (position: number, total: number) =>
    `Factura ${String(position)} de ${String(total)}`,
  /** Al terminar uno, se salta al siguiente. */
  finishedNext: (done: string, next: string) =>
    `Terminaste con ${done}. Sigue ${next}.`,
  finishedAll: (done: string) =>
    `Terminaste con ${done}. No quedan más participantes con pendientes.`,
  startWith: (name: string) => `Empiezas con ${name}.`,
  /** Lo que decide en Socio Amigo, en el panel de datos. */
  checks: "Lo que decide",
  duplicateAlert: "Posible duplicada",
  byReviewer: "Decidido por el digitador",
  /** lala4 la devolvió por sus datos. */
  returnedTitle: "lala4 devolvió esta factura",
  returnedHint: "Corrige los datos y vuelve a decidir.",
  /** En la tira: lo que dijo lala4, y que el documento volvió a la cola. */
  returnedShort: (what: string) => `Devuelta · ${what}`,
} as const;

export const MONTH_NAMES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
] as const;

export const TEXTS = {
  title: "Digitación",
  uploadMonth: "Mes de carga",
  uploadMonthHint:
    "Los participantes, los pendientes, el avance a la meta y la siguiente factura son de las facturas subidas en este mes.",
  uploadMonthShown: (label: string) => `Mes de carga: ${label}`,
  work: "Work",
  batch: "Confirmar en lote",
  batchTitle: "Confirmar en lote",
  batchHint:
    "En orden de subida y con los datos tal como están. Entran las pre-aprobadas y las pre-rechazadas de confianza alta; las demás dicen por qué no. La decisión sigue siendo tuya: aquí solo confirmas lo que ya viste.",
  batchList: "Lo que se confirmaría",
  batchRowsHint: "recorren; la letra cambia el motivo:",
  batchRow: (id: string) => `Factura ${id}`,
  batchReason: (id: string) => `El motivo de la factura ${id}`,
  batchChanged: "cambiado",
  batchProposedWas: (label: string) => `Proponía: ${label}`,
  batchChangedCount: (count: number) =>
    count === 1
      ? "1 con el motivo cambiado."
      : `${String(count)} con el motivo cambiado.`,
  batchConfirm: (count: number) => `Confirmar ${String(count)}`,
  batchConfirmed: (count: number) =>
    count === 1
      ? "Se confirmó 1 factura."
      : `Se confirmaron ${String(count)} facturas.`,
  batchSkipped: (count: number) =>
    count === 1 ? "Se saltó 1:" : `Se saltaron ${String(count)}:`,
  batchSkipWhy: (label: string, message: string) => `${label}: ${message}`,
  batchDone: (count: number) =>
    count === 1
      ? "1 factura confirmada en lote."
      : `${String(count)} facturas confirmadas en lote.`,
  batchNothing:
    "Este participante no tiene facturas pendientes para confirmar en lote.",
  done: "Listo",
  cancel: "Cancelar",
  loadMore: "Cargar más",
  takeNext: "Tomar siguiente",
  takeNextHint:
    "Toma la factura más antigua del participante que espera digitación y nadie tiene tomado; si ya no queda, pasa al siguiente participante.",
  nothingOpen: "Elige un participante, o toma el siguiente, para empezar.",
  noneLeft: "No quedan facturas por digitar.",
  /** El nombre de un documento, por su identificador. */
  document: (id: string) => `Factura ${id}`,
  uploaded: "Subido",
  /** El title de la fecha de subida en la cola, con la fecha entera. */
  uploadedAt: (when: string) => `Subido ${when}`,
  participant: "Participante",
  participantId: "Identificación",
  participantKey: "ID participante",
  /** El distribuidor (grupo) del participante. */
  group: "Distribuidor",
  period: (label: string, source: "issued" | "uploaded") =>
    `${label} (${source === "issued" ? "por la fecha de la factura" : "por la fecha de subida"})`,
  photoFailed: "No se pudo cargar la foto.",
  /** Un PDF cuya imagen el back aún dibuja: llega con el refresco. */
  photoPreparing: "Preparando la imagen…",
  loading: "Cargando…",
  crop: "Recorte",
  fullPhoto: "Foto completa",
  noSection: "No vino esta sección: se ve la foto completa.",
  boxes: "Recuadros",
  boxesShown: "Recuadros visibles",
  boxesHidden: "Recuadros ocultos",
  data: "Los datos",
  lines: "Líneas",
  noLines: "No se leyó ninguna línea.",
  addedByHand: "Agregada a mano",
  /** Los tres importes y el subtotal por ENCIMA de cantidad × unitario. */
  amountsMismatch: "El subtotal no cuadra: es mayor que cantidad × unitario",
  /** El subtotal por debajo: un descuento, y está bien. Truncado. */
  discount: (share: number) =>
    `Descuento ${String(Math.floor(share * 100 + 1e-9))} %`,
  discountHint: (amount: string, gross: string) =>
    `Cantidad × unitario daría ${gross}; el subtotal es ${amount} menos`,
  /** Lo que se saca de los otros dos importes: en gris, nunca escrito. */
  calculated: "calculado",
  unitFromTotal: "Calculado: subtotal ÷ cantidad",
  totalFromUnit: "Calculado: cantidad × unitario",
  suggested: "Sugerido",
  edited: "Corregido",
  /** El dato que la persona dio por bueno. */
  readValidated: "Lectura validada",
  lowConfidence: "Confianza baja",
  mediumConfidence: "Confianza media",
  /** Alt+A sin nada que mirar. */
  noAttention: "Nada más pide atención en esta factura.",
  /** Junto al dato al que apunta un chequeo que no quedó «Bien». */
  review: "Revisar",
  /** La sección con todos sus chequeos «Bien». */
  validated: "Validado",
  /** La sección con chequeos que piden mirar: «1 revisión», «2 revisiones». */
  reviews: (count: number) =>
    count === 1 ? "1 revisión" : `${String(count)} revisiones`,
  /** Para el lector: «Chequeos: Emisor». */
  checksOf: "Chequeos",
  revert: (value: string) =>
    value === ""
      ? "Esc lo deja vacío, como se leyó"
      : `Esc vuelve a «${value}»`,
  notADate: "No es una fecha: escríbela como dd/mm/aaaa.",
  decision: "Decisión",
  reason: "Motivo",
  chooseReason: "Elige el motivo",
  /** ¿Hay producto ADELCA en la factura? */
  productQuestion: "¿Hay producto ADELCA?",
  productYes: "Sí",
  productNo: "No",
  /** Un documento 100 % manual: el OCR no encontró ninguna línea ADELCA. */
  productGroupTitle: "¿Qué grupo es?",
  productGroupHint: "1 a 5 elige; Esc cancela",
  productGroupLoading: "Cargando los grupos…",
  productGroupFailed: "No se pudieron cargar los grupos.",
  productGroupMissing: "Elige el grupo antes de confirmar.",
  save: "Guardar y seguir",
  /** La pre-revisión solo guía: confirmarla es decisión del digitador. */
  proposal: "Lo que propone el sistema",
  confirm: "Confirmar",
  willSave: (result: string) =>
    `Guarda «${result}» con los datos tal como están y pasa al siguiente.`,
  claimedByMe: (until: string) => `Tomado por ti hasta las ${until}`,
  claimedByOther: (by: string) => `Lo está viendo ${by}`,
  unclaimed: "Sin tomar: se toma al guardar",
  readOnly: "Solo lectura",
  decided: (label: string, by: string) => `Decidido: ${label}, por ${by}`,
  needDecision: "Elige la decisión: 1 Válida, 2 Inválida o 3 Para información.",
  /** Válida con el Detalle en «No» o sin responder: no se puede guardar así. */
  participatesRequired: "Hay que decir si hay producto ADELCA, en el Detalle.",
  needReason: "Elige el motivo de la inválida.",
  needNote: (reason: string) =>
    `«${reason}» pide una nota: escríbela y pulsa Enter.`,
  note: "Nota",
  notePlaceholder: "Qué tiene la factura",
  duplicateOf: "Duplicada de…",
  duplicateSearching: "Buscando candidatas…",
  duplicateNone:
    "No se encontró otra factura de este participante: se guarda como Duplicada sin indicar cuál.",
  duplicateHint: "↑↓ elige, Enter confirma. Esc vuelve a los motivos.",
  duplicateCandidate: (candidate: SearchedDuplicate) =>
    `#${candidate.number ?? "?"}${
      candidate.issuedOn === null ? "" : ` · ${formatDate(candidate.issuedOn)}`
    }${
      candidate.distributorName === null
        ? ""
        : ` · ${candidate.distributorName}`
    }${
      candidate.decision === null
        ? ""
        : ` · ${CANDIDATE_DECISION_LABELS[candidate.decision]}`
    }`,
  duplicateOfItself: "Un documento no puede ser duplicado de sí mismo.",
  /**
   * «Elena Rivas · 1710000001», lo que haya; sin nada (la candidata solo
   * viene del historial de lala4, sin réplica), lo dice.
   */
  duplicateParticipant: (candidate: {
    readonly participantName: string | null;
    readonly participantIdentifier: string | null;
    readonly participantGroupName: string | null;
  }): string => {
    const parts = [
      candidate.participantName,
      candidate.participantIdentifier,
      candidate.participantGroupName,
    ].filter((part): part is string => part != null && part !== "");
    return parts.length === 0
      ? "Participante no disponible (historial de lala4)"
      : parts.join(" · ");
  },
  duplicatesSameParticipant: "Mismo participante",
  /** «Subida 13/10/2026 23:13», hora de Ecuador (`formatDateTime`). */
  duplicatesUploaded: (when: string) => `Subida ${when}`,
  /** El diálogo en mosaico (Alt+B ya es de Alfred: se abre por botón). */
  duplicatesTitle: "¿Es duplicada?",
  duplicatesHint:
    "A la izquierda, esta factura. A la derecha, la(s) que se le parecen.",
  duplicatesEmpty: "No se encontró otra factura que se le parezca.",
  duplicatesTooShort: "Escribe más dígitos para buscar.",
  /** `candidate.photo === null`: solo existe en el historial de lala4. */
  duplicatesNoPhoto: "Solo en el historial de lala4, sin foto.",
  duplicatesSearchLabel: "Buscar por número",
  duplicatesSearchPlaceholder: "Número de la factura…",
  duplicatesKeysHint:
    "↑↓ elige · Enter o un número es «Duplicada de…» · N descarta la resaltada como «No es duplicada» · Esc cierra sin decidir",
  duplicatesNotDuplicate:
    "No es duplicada: decide la factura como de costumbre.",
  duplicatesRuledOut: (id: string) =>
    `${id}: no es duplicada. Sigue sin proponerse.`,
  openDuplicates: "Ver posibles duplicadas",
  notWaiting: (where: string) =>
    `Todavía no le toca a un digitador (${where}): solo lectura.`,
  release: "Soltar",
  released: (id: string) =>
    `Factura ${id}: soltada, ya la puede tomar otra persona.`,
  missing: (what: string) => `Para guardarlo como Válido falta ${what}.`,
  saved: (id: string, decision: string) => `Factura ${id}: ${decision}.`,
  changed: (count: number) =>
    count === 0
      ? "Sin correcciones."
      : count === 1
        ? "1 dato corregido."
        : `${String(count)} datos corregidos.`,
  fullscreen: "Pantalla completa",
  exitFullscreen: "Salir de pantalla completa",
  fullWidth: "Ancho completo",
  exitFullWidth: "Ver los participantes",
  closeDocument: "Cerrar la factura",
  savingOne: (id: string) => `Guardando la factura ${id}…`,
  waitingClaim: "Esperando a que esta factura sea tuya: un momento.",
  takenByOther: (id: string) => `El ${id} lo tomó otra persona.`,
  screenExits: "Salir de la pantalla completa",
  previousDocument: "Factura anterior",
  nextDocument: "Factura siguiente",
  backToWork: "Volver a Work ▸ Facturas",
  unsavedTitle: "Tienes cambios sin guardar",
  unsavedBody:
    "¿Salir igual? Lo que escribiste sin guardar se pierde. Enter te deja aquí.",
  unsavedStay: "Quedarme",
  unsavedLeave: "Salir sin guardar",
  /** Al guardar como Válida con la pre-revisión o una marca «Duplicada de…»
      todavía sin resolver (101-grados/socio-amigo-admin-ui#8). */
  duplicateConfirmTitle: "¿Seguro que no es duplicada?",
  duplicateConfirmBody:
    "Esta factura se marcó como posible duplicada de otra. Si de verdad es distinta, Confirmar la guarda como Válida.",
  duplicateConfirmOriginal: "La original",
  duplicateConfirmLoading: "Buscando la original…",
  duplicateConfirmBack: "Volver",
  duplicateConfirmSave: "Confirmar",
  palette: "Menú de comandos",
  paletteSearch: "Escribe un comando o el número de una factura…",
  paletteEmpty: "Nada coincide.",
  shortcuts: "Atajos de teclado",
  original: "La foto tal como llegó",
  originalPdfTitle: "El PDF tal como llegó",
  originalPdf:
    "El archivo que subió el participante. En los recortes se ve dibujado como imagen.",
  /* ── Deshacer y rehacer ── */
  /** Lo retenido: todavía no salió. */
  held: (id: string, label: string) =>
    `Factura ${id}: ${label}. Se guarda en unos segundos.`,
  /** Abierto desde la cola mientras espera: bloqueado, con «Deshacer». */
  heldLocked: (label: string) => `${label}: se guarda en unos segundos`,
  heldSending: (label: string) => `${label}: guardándose`,
  undone: (id: string) =>
    `Factura ${id}: deshecha. Sigue sin decidir, con lo que habías escrito.`,
  redone: (id: string, label: string) =>
    `Factura ${id}: ${label} otra vez. Se guarda en unos segundos.`,
  heldFailed: (id: string, why: string) =>
    `La decisión de la factura ${id} no se guardó: ${why}`,
  heldOffline: (id: string) =>
    `Sin conexión: la decisión de la factura ${id} se guardará al volver.`,
  recovered: (ids: readonly string[]) =>
    ids.length === 1
      ? `Se guardó la decisión pendiente de la factura ${ids[0] ?? ""}.`
      : `Se guardaron las decisiones pendientes de las facturas ${ids.join(", ")}.`,
  recoveredOld: (count: number) =>
    count === 1
      ? "Una decisión pendiente era de hace más de 15 minutos y se descartó: esa factura sigue sin decidir."
      : `${String(count)} decisiones pendientes eran de hace más de 15 minutos y se descartaron: esas facturas siguen sin decidir.`,
  nothingToUndo: "Nada que deshacer.",
  nothingToRedo: "Nada que rehacer.",
  redoStale:
    "Cambiaste la factura después de deshacer: guárdala con lo nuevo, ya no se rehace.",
  releaseFailed: (id: string) =>
    `No se pudo soltar la factura ${id}: queda tomada por ti unos minutos.`,
  undo: "Deshacer",
  redo: "Rehacer",
  openFailed: (id: string) => `Abrir ${id}`,
  undoTitle: (what: string, seconds: number) =>
    `Deshacer: ${what} (se guarda en ${String(seconds)} s)`,
  redoTitle: (what: string, seconds: number) =>
    `Rehacer: ${what} (${String(seconds)} s para rehacer)`,
  openFailedTitle: (id: string) =>
    `Abrir la factura ${id} para corregir su decisión`,
  undoWhat: {
    decide: (id: string, label: string) => `Factura ${id}, ${label}`,
  },
  heldInQueue: "Se guarda en unos segundos: se puede deshacer",
  noPermission:
    "No tienes permiso para ver esta sección. Si crees que deberías, pídeselo a quien administra el programa.",
  mockBadge: "Datos de prueba",
  mock: "Con datos de prueba: el API de la digitación todavía no está. Lo que se guarda vive en esta pestaña.",
} as const;

/**
 * Los nombres de pantalla de los campos que nombra un `fieldErrors` del API
 * (`field-errors.ts`).
 */
export const FIELD_ERROR_LABELS: Partial<Record<string, string>> = {
  issuedAt: "Fecha",
  documentNumber: "Número",
  issuer: "Emisor",
  lines: "Líneas",
  product: "producto",
  description: "descripción",
  quantity: "cantidad",
  unitPrice: "unitario",
  lineTotal: "subtotal",
  reasonKey: "Motivo",
  note: "Nota",
};

/** La línea que nombra un `fieldErrors` (`lines.N`), por su lugar. */
export const fieldErrorLine = (line: number): string => `Línea ${String(line)}`;

/** Lo que falta, dicho como se dice. */
export const MISSING_LABELS = {
  number: "el número",
  date: "la fecha",
} as const;
